import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Sparkles, Loader2, AlertCircle, RefreshCw, Bot, ListChecks } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import PageHeader from '@/components/PageHeader';

function MarkdownLite({ source }) {
  // Lightweight markdown renderer for headings (## / ###), bullets, bold, code.
  // Avoids adding a new dependency. Output is read-only admin content so the
  // injection risk is negligible (sanitised by escaping HTML first).
  const html = useMemo(() => {
    if (!source) return '';
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const lines = esc(source).split('\n');
    const out = [];
    let inList = false;
    for (let raw of lines) {
      let line = raw;
      // bold + italic + inline code
      line = line.replace(/`([^`]+)`/g, '<code class="bg-slate-100 rounded px-1 py-0.5 text-xs">$1</code>');
      line = line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      line = line.replace(/_([^_]+)_/g, '<em class="text-slate-600">$1</em>');
      if (/^##\s+/.test(line)) {
        if (inList) { out.push('</ul>'); inList = false; }
        out.push(`<h2 class="text-lg font-bold text-slate-900 mt-5 mb-2">${line.replace(/^##\s+/, '')}</h2>`);
        continue;
      }
      if (/^###\s+/.test(line)) {
        if (inList) { out.push('</ul>'); inList = false; }
        out.push(`<h3 class="text-sm font-semibold text-slate-800 mt-3 mb-1">${line.replace(/^###\s+/, '')}</h3>`);
        continue;
      }
      if (/^[-*]\s+/.test(line)) {
        if (!inList) { out.push('<ul class="list-disc pl-5 space-y-1">'); inList = true; }
        out.push(`<li class="text-sm text-slate-700">${line.replace(/^[-*]\s+/, '')}</li>`);
        continue;
      }
      if (/^\d+\.\s+/.test(line)) {
        if (!inList) { out.push('<ol class="list-decimal pl-5 space-y-1">'); inList = true; }
        out.push(`<li class="text-sm text-slate-700">${line.replace(/^\d+\.\s+/, '')}</li>`);
        continue;
      }
      if (line.trim() === '') {
        if (inList) { out.push('</ul>'); inList = false; }
        out.push('<div class="h-2"></div>');
        continue;
      }
      out.push(`<p class="text-sm text-slate-700 leading-relaxed">${line}</p>`);
    }
    if (inList) out.push('</ul>');
    return out.join('\n');
  }, [source]);

  // eslint-disable-next-line react/no-danger
  return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} data-testid="prateek-briefing-body" />;
}

function ProviderStatusBadge({ status }) {
  if (!status) return null;
  if (!status.configured) {
    return (
      <Badge variant="outline" className="border-rose-300 text-rose-700 bg-rose-50" data-testid="prateek-status-missing">
        <AlertCircle className="h-3 w-3 mr-1" /> AI key missing
      </Badge>
    );
  }
  const label = status.using_emergent_key ? 'Emergent universal key' : 'Custom key';
  return (
    <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50" data-testid="prateek-status-ok">
      <Bot className="h-3 w-3 mr-1" /> {status.provider} / {status.model} • {label}
    </Badge>
  );
}

function BriefingCard({ report }) {
  if (!report) return null;
  return (
    <Card className="p-6 border-slate-200" data-testid="prateek-briefing-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">Daily Executive Briefing</p>
            <p className="text-[11px] text-slate-500" data-testid="prateek-briefing-time">
              Generated {formatDate(report.created_at)}
            </p>
          </div>
        </div>
        <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
          {report.type?.replace(/_/g, ' ')}
        </Badge>
      </div>
      <MarkdownLite source={report.final_briefing} />
    </Card>
  );
}

function HistoryList({ reports, onSelect, activeId }) {
  if (!reports || reports.length === 0) return null;
  return (
    <Card className="p-4 border-slate-200" data-testid="prateek-history">
      <div className="flex items-center gap-2 mb-3">
        <ListChecks className="h-4 w-4 text-slate-500" />
        <p className="text-sm font-semibold text-slate-900">Past briefings</p>
      </div>
      <ul className="divide-y divide-slate-100">
        {reports.map((r) => (
          <li key={r.id}>
            <button
              onClick={() => onSelect(r)}
              data-testid={`prateek-history-item-${r.id}`}
              className={`w-full text-left px-2 py-2 rounded-md text-sm hover:bg-slate-50 transition-colors ${
                activeId === r.id ? 'bg-rose-50 text-rose-700' : 'text-slate-700'
              }`}
            >
              {formatDate(r.created_at)}
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function PrateekAI() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const [history, setHistory] = useState([]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get('/admin/prateek-ai/status');
      setStatus(data);
    } catch (e) {
      setStatus({ configured: false, detail: e?.response?.data?.detail || 'Status check failed' });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/prateek-ai/reports', { params: { limit: 10 } });
      setHistory(data || []);
      if (!activeReport && data && data.length > 0) {
        setActiveReport(data[0]);
      }
    } catch (e) {
      // Non-fatal — history is optional decoration.
      if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Failed to load Prateek history:', e?.message || e);
      }
    }
  }, [activeReport]);

  useEffect(() => {
    loadStatus();
    loadHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const { data } = await api.post('/admin/prateek-ai/generate');
      setActiveReport(data);
      setHistory((prev) => [data, ...prev].slice(0, 10));
      toast.success('Briefing ready');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to generate briefing';
      setError(detail);
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  }, []);

  const aiKeyMissing = status && status.configured === false;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Prateek AI Reports"
        subtitle="Internal admin briefings on user activity, bookings, revenue and local marketing ideas."
        onRefresh={loadHistory}
        loading={statusLoading || generating}
        refreshTestId="prateek-refresh"
      />

      <Card className="p-4 border-slate-200">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Prateek AI — Daily Executive Briefing</p>
              <p className="text-xs text-slate-500">Reads aggregated business metrics. Never touches PII or payment data.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ProviderStatusBadge status={status} />
            <Button
              onClick={generate}
              disabled={generating || aiKeyMissing}
              data-testid="btn-generate-briefing"
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {generating ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
              ) : (
                <><RefreshCw className="h-4 w-4 mr-2" /> Generate Daily Briefing</>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {aiKeyMissing && (
        <Alert variant="destructive" data-testid="prateek-key-missing">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>AI provider key missing</AlertTitle>
          <AlertDescription>
            {status?.detail || 'Add OPENAI_API_KEY, ANTHROPIC_API_KEY, or EMERGENT_LLM_KEY to backend/.env and restart the backend.'}
          </AlertDescription>
        </Alert>
      )}

      {error && !aiKeyMissing && (
        <Alert variant="destructive" data-testid="prateek-error">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to generate briefing</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {generating && !activeReport && (
            <Card className="p-10 border-slate-200 text-center text-slate-500 text-sm" data-testid="prateek-loading">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Prateek AI is reading the books…
            </Card>
          )}
          {activeReport && <BriefingCard report={activeReport} />}
          {!activeReport && !generating && (
            <Card className="p-10 border-dashed border-slate-300 text-center text-slate-500" data-testid="prateek-empty">
              <Sparkles className="h-6 w-6 mx-auto text-slate-300 mb-2" />
              <p className="text-sm">No briefing yet. Click “Generate Daily Briefing” to create one.</p>
            </Card>
          )}
        </div>
        <div>
          <HistoryList
            reports={history}
            activeId={activeReport?.id}
            onSelect={setActiveReport}
          />
        </div>
      </div>
    </div>
  );
}
